if (!customElements.get('vioka-welcome-popup')) {
  customElements.define('vioka-welcome-popup', class extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) this.initialize();
      this.connect();
    }

    initialize() {
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.error = this.querySelector('[role="alert"]');
      this.designMode = this.dataset.designMode === 'true';
      this.subscribedKey = 'vioka:welcome:subscribed';
      this.dismissedKey = 'vioka:welcome:dismissed-until';
      this.onStorage = () => {
        if (this.isSuppressed() && this.dialog.open) this.dialog.close();
      };
      this.onPageShow = (event) => {
        if (event.persisted && this.isSuppressed() && this.dialog.open) this.dialog.close();
      };
      this.onSectionSelect = (event) => {
        if (event.target.contains(this)) this.open();
      };

      this.querySelectorAll('[data-dismiss]').forEach((button) => {
        button.addEventListener('click', () => this.dismiss());
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dismiss();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(this.dialog.querySelectorAll('button, input, a[href]'))
          .filter((control) => !control.disabled && control.getClientRects().length);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dismiss();
      });
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.form.querySelector('[name="name"]').addEventListener('input', (event) => event.target.setCustomValidity(''));
      this.form.querySelector('[type="submit"]').disabled = false;
      this.querySelector('[data-copy]').addEventListener('click', () => this.copyCode());
    }

    connect() {
      window.addEventListener('storage', this.onStorage);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('shopify:section:select', this.onSectionSelect);

      if (this.designMode) {
        this.open();
        return;
      }

      if (!this.isSuppressed()) {
        const delay = Number(this.dataset.delay);
        this.timer = window.setTimeout(() => {
          if (!this.isSuppressed()) this.open();
        }, Number.isFinite(delay) ? Math.max(0, delay) : 10000);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      if (this.dialog?.open) this.dialog.close();
    }

    readStorage(storage, key) {
      try {
        return window[storage].getItem(key);
      } catch {
        return null;
      }
    }

    writeStorage(storage, key, value) {
      if (this.designMode) return;
      try {
        if (value === null) window[storage].removeItem(key);
        else window[storage].setItem(key, value);
      } catch {}
    }

    isSuppressed() {
      if (this.designMode) return false;
      return this.readStorage('localStorage', this.subscribedKey) === '1'
        || Number(this.readStorage('localStorage', this.dismissedKey)) > Date.now();
    }

    open() {
      if (!this.dialog.open && this.dialog.showModal) {
        this.dialog.showModal();
        this.querySelector('.vioka-popup__heading').focus({ preventScroll: true });
      }
    }

    dismiss() {
      const days = Number(this.dataset.dismissDays);
      const duration = (Number.isFinite(days) && days > 0 ? days : 7) * 24 * 60 * 60 * 1000;
      this.writeStorage('localStorage', this.dismissedKey, String(Date.now() + duration));
      this.dialog.close();
    }

    async submit(event) {
      event.preventDefault();
      if (this.designMode || this.form.getAttribute('aria-busy') === 'true') return;
      const name = this.form.querySelector('[name="name"]');
      name.value = name.value.trim();
      if (!name.value) {
        name.setCustomValidity(name.dataset.nameRequired);
        name.reportValidity();
        return;
      }
      this.error.hidden = true;
      this.form.setAttribute('aria-busy', 'true');
      const button = this.form.querySelector('[type="submit"]');
      button.disabled = true;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 30000);
      try {
        if (!this.dataset.signupUrl) throw new Error('Missing signup endpoint');
        const endpoint = new URL(this.dataset.signupUrl, location.origin);
        const localPreview = ['127.0.0.1', 'localhost'].includes(location.hostname) && endpoint.origin === location.origin;
        if (endpoint.protocol !== 'https:' && !localPreview) throw new Error('Invalid signup endpoint');
        const response = await fetch(endpoint.href, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          redirect: 'error',
          referrerPolicy: 'no-referrer',
          signal: controller.signal,
          body: JSON.stringify({
            name: name.value,
            email: this.form.querySelector('[name="email"]').value.trim(),
            website: this.form.querySelector('[name="website"]').value
          })
        });
        const result = await response.json();
        if (!response.ok || result.ok !== true || result.code !== 'VIOKA5' || result.code !== this.querySelector('[data-code]').textContent.trim()) {
          throw new Error('Signup not confirmed');
        }
        this.writeStorage('localStorage', this.subscribedKey, '1');
        this.form.reset();
        this.showSuccess();
        if (this.dialog.open) this.querySelector('.vioka-popup__heading').focus({ preventScroll: true });
      } catch {
        this.error.hidden = false;
      } finally {
        window.clearTimeout(timeout);
        this.form.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }

    showSuccess() {
      this.querySelectorAll('[data-signup]').forEach((element) => { element.hidden = true; });
      this.querySelectorAll('[data-success]').forEach((element) => { element.hidden = false; });
    }

    async copyCode() {
      const button = this.querySelector('[data-copy]');
      const status = this.querySelector('[data-copy-status]');
      try {
        await navigator.clipboard.writeText(this.querySelector('[data-code]').textContent.trim());
        status.textContent = button.dataset.copied;
      } catch {
        status.textContent = button.dataset.copyFailed;
      }
    }
  });
}
